import * as Sentry from '@sentry/react'
import { create } from 'zustand'
import { adminApi } from '../services/api'
import { resolvePermissionsForRole } from '../lib/permissions'

const TOKEN_KEY = 'rapidry_admin_token'
const USER_KEY = 'rapidry_admin_user'

export interface AuthUser {
  id: string
  name: string
  email: string
  role: string
  admin_role?: string
  permissions?: string[]
}

interface LoginResponse {
  success: boolean
  data: {
    token: string
    user: AuthUser
  }
}

interface AuthState {
  token: string | null
  user: AuthUser | null
  isAuthenticated: boolean
  hydrateUser: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  logout: () => void
}

const initialToken = localStorage.getItem(TOKEN_KEY)

const normalizeUser = (user: AuthUser | null): AuthUser | null => {
  if (!user) return null

  const adminRole = user.role === 'admin' ? user.admin_role || 'owner' : user.admin_role
  // Prefer server-provided permissions; fall back to deriving them from the
  // admin role so token-only hydration still gates the UI correctly.
  const permissions =
    user.permissions && user.permissions.length > 0
      ? user.permissions
      : resolvePermissionsForRole(adminRole)

  return {
    ...user,
    admin_role: adminRole,
    permissions,
  }
}

const parseStoredUser = () => {
  try {
    return normalizeUser(JSON.parse(localStorage.getItem(USER_KEY) || 'null') as AuthUser | null)
  } catch {
    localStorage.removeItem(USER_KEY)
    return null
  }
}

const decodeUserFromToken = (token: string | null): AuthUser | null => {
  if (!token) return null

  try {
    const [, payload] = token.split('.')
    if (!payload) return null

    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/')
    const decoded = JSON.parse(atob(normalizedPayload)) as {
      userId?: string
      role?: string
      admin_role?: string | null
    }

    if (!decoded.userId || !decoded.role) return null

    return normalizeUser({
      id: decoded.userId,
      name: 'Admin',
      email: '',
      role: decoded.role,
      admin_role: decoded.admin_role || undefined,
    })
  } catch {
    return null
  }
}

const initialUser = parseStoredUser() || decodeUserFromToken(initialToken)

export const useAuthStore = create<AuthState>((set) => ({
  token: initialToken,
  user: initialUser,
  isAuthenticated: Boolean(initialToken),

  hydrateUser: async () => {
    if (!localStorage.getItem(TOKEN_KEY)) return

    try {
      const response = await adminApi.getMe()
      const user = normalizeUser(response.data ?? response)

      if (user) {
        localStorage.setItem(USER_KEY, JSON.stringify(user))
        Sentry.setUser({ id: user.id, role: user.admin_role || user.role })
        set({ user, isAuthenticated: true })
      }
    } catch {
      // The API interceptor handles invalid tokens. Keep the token-derived role
      // meanwhile so navigation does not flicker or lose owner-only entries.
    }
  },

  login: async (email: string, password: string) => {
    const response = (await adminApi.login(email, password)) as LoginResponse
    const token = response.data?.token ?? null
    const user = normalizeUser(response.data?.user ?? null)

    if (!token || !user) {
      throw new Error('Invalid login response')
    }

    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(USER_KEY, JSON.stringify(user))
    Sentry.setUser({ id: user.id, role: user.admin_role || user.role })
    set({
      token,
      user,
      isAuthenticated: true,
    })
  },

  logout: () => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    Sentry.setUser(null)
    set({
      token: null,
      user: null,
      isAuthenticated: false,
    })
    window.location.assign('/login')
  },
}))
